<template>
  <div
    v-if="enrollmentRequests"
    class="d-flex flex-column ga-4"
    data-testId="interested-table"
  >
    <div class="d-flex flex-md-row flex-column align-center">
      <v-col
        class="pa-0 mb-4 mb-md-0"
        cols="12"
        md="4"
      >
        <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledTextfield -->
        <v-text-field
          v-model="query"
          :aria-label="$bct('Search families and children by name')"
          :placeholder="$bct('Search families and children by name')"
          density="compact"
          prepend-inner-icon="search"
          variant="solo-filled"
          clearable
          flat
          hide-details
        />
      </v-col>

      <v-col
        class="pa-0 d-flex justify-end ga-2"
        cols="12"
        md="8"
      >
        <v-tooltip
          v-if="displayCocVerification"
          :disabled="isVerifyButtonEnabled"
          :text="verifyButtonTooltip"
          location="bottom"
        >
          <template #activator="{ props: tooltipProps }">
            <div
              v-bind="tooltipProps"
              class="flex-grow-1 flex-md-grow-0"
            >
              <v-btn
                @click="openVerifyDialog"
                :disabled="!isVerifyButtonEnabled"
                class="w-100 w-md-auto"
                color="primary"
                size="default"
                variant="flat"
              >
                {{ $bct('Verify child') }}
              </v-btn>
            </div>
          </template>
        </v-tooltip>

        <v-btn
          v-if="store.state.pages.Interested.features.enable_create_new"
          @click="newDialog.open()"
          :aria-label="$bct('Create new')"
          class="flex-grow-1 flex-md-grow-0"
          color="primary"
          data-cy="add-button"
          prepend-icon="add"
          variant="flat"
        >
          {{ $bct('Add new child') }}
        </v-btn>

        <v-btn
          v-if="selectedSubsidyProgram?.enable_provider_program_referral_links === true"
          @click="$refs.inviteDialog.open()"
          class="focus-very-visible flex-grow-1 flex-md-grow-0"
          color="primary"
          data-testid="provider-invite-btn"
          prepend-icon="people"
          tabindex="0"
          variant="flat"
        >
          <span class="pl-2">
            {{ $bct('Invite') }}
          </span>
        </v-btn>
      </v-col>
    </div>

    <v-row class="d-flex align-center ma-0">
      <v-col
        class="d-flex align-center pa-0"
        cols="12"
        md="6"
      >
        <div class="d-flex ga-3">
          <v-menu>
            <template #activator="{ props: menuProps }">
              <v-btn
                v-show="displayEditCheckedButton"
                v-bind="menuProps"
                :loading="processing"
                color="primary"
                data-cy="edit-button"
                size="small"
                variant="flat"
              >
                {{ $bct('Edit') }}
              </v-btn>
            </template>

            <v-list
              v-show="displayEditCheckedOptions"
              data-testid="edit-checked-options"
            >
              <v-list-item
                v-for="status in availablePendingStatuses"
                @click="setStatusForAll(status.value)"
                :key="status.value"
              >
                <v-list-item-title>
                  <span class="me-1">
                    {{ $bct('Change to') }}
                  </span>

                  <span>{{ $bct(status.text) }}</span>
                </v-list-item-title>
              </v-list-item>

              <v-list-item
                @click="draftEnrollAll()"
                :disabled="enrollAllDisabled"
              >
                <v-list-item-title>{{ $bct(terms.enroll) }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <v-btn
            v-if="store.state.pages.Interested.features.enable_mass_messaging"
            v-show="checkedEnrollmentRequests.length > 0"
            @click="$refs.newMessageDialog.open()"
            :loading="processing"
            color="primary"
            data-cy="message-button"
            data-testid="message-button"
            size="small"
            variant="flat"
          >
            {{ $bct('Message') }}
          </v-btn>

          <v-btn
            v-show="areValidEnrollmentsWithoutSubsidyPrograms()"
            @click="$refs.transferSelectedEnrollmentsDialog.open()"
            :loading="processing"
            color="primary"
            data-cy="transfer-button"
            size="small"
            variant="flat"
          >
            {{ $bct('Transfer') }}
          </v-btn>
        </div>
      </v-col>
    </v-row>

    <div>
      <v-card
        class="bb-0"
        border
        tile
      >
        <v-card-text class="fs-15">
          <v-row
            class="d-flex flex-md-row flex-column justify-space-between"
            dense
          >
            <v-col class="d-flex flex-md-row flex-column align-start align-md-center mb-4 mb-md-0">
              <template v-if="filterSummary">
                <div class="mb-4 mb-md-0">
                  <v-icon
                    icon="filter_alt"
                    start
                  />
                  <span class="me-1"> {{ $bct('Filtering by') }}: </span>
                  <span class="d-inline">
                    <span class="d-inline fw-600">
                      {{ filterSummary }}
                    </span>
                    <span>.&nbsp;</span>
                  </span>
                </div>
              </template>
              <span>
                <span v-if="filters.page == 1">
                  {{ $bct('Displaying first') }} <strong>{{ enrollmentRequests?.length }}</strong>
                  {{ $bct('of') }} <strong>{{ totalItemCount }}</strong> {{ $bct('results') }}.
                </span>
                <span v-else>
                  {{ $bct('Displaying results') }}
                  <strong>
                    {{ pageStartCount }}-{{ pageStartCount + enrollmentRequests.length - 1 }}
                  </strong>
                  {{ $bct('out of') }} <strong>{{ totalItemCount }}</strong> {{ $bct('results') }}.
                </span>
              </span>
            </v-col>

            <v-col class="d-flex justify-space-between justify-md-end align-center">
              <div class="d-flex flex-md-row flex-column justify-space-between justify-md-end">
                <div class="d-flex mb-2 mb-md-0 justify-space-between justify-md-start">
                  <FilterMenu
                    v-if="displayCocVerification"
                    :actions="false"
                    :title="filteredVerificationStatus || 'Verification'"
                    elevation="0"
                    test-id="verification-filter"
                    width="200"
                    borderless
                    left
                    outlined
                    paddingless
                  >
                    <template #card>
                      <v-list v-model:selected="filters.verification_status">
                        <v-list-item
                          v-for="status in verificationStatus"
                          :key="status.value"
                          :title="$bct(status.text)"
                          :value="status.value"
                          color="primary"
                          data-cy="verification-checkmark"
                        >
                          <template #prepend="{ isSelected, select }">
                            <v-checkbox-btn
                              @update:model-value="select"
                              :model-value="isSelected"
                              density="compact"
                              tabindex="-1"
                            >
                              <template #input="{ inputNode, icon }">
                                <v-icon
                                  :icon="icon"
                                  size="x-small"
                                />

                                <component :is="inputNode" />
                              </template>
                            </v-checkbox-btn>
                          </template>
                        </v-list-item>
                      </v-list>
                    </template>
                  </FilterMenu>

                  <FilterMenu
                    ref="providerFilter"
                    :actions="false"
                    :title="filteredProviderName || 'Location'"
                    elevation="0"
                    width="400"
                    borderless
                    left
                    outlined
                  >
                    <template #card>
                      <v-autocomplete
                        v-model="filters.provider_id"
                        @update:model-value="$refs.providerFilter.close()"
                        :item-title="getProviderNameWithAddress"
                        :items="providers"
                        :placeholder="$bct('Search by name or address')"
                        item-value="id"
                        variant="filled"
                        auto-select-first
                        clearable
                        left
                      />
                    </template>
                  </FilterMenu>
                </div>

                <div class="d-flex justify-space-between justify-md-start">
                  <FilterMenu
                    v-if="
                      selectedSubsidyProgram?.enable_deferred_acceptance && filteredProviderName
                    "
                    :actions="false"
                    :title="filteredProgramName || terms.program"
                    elevation="0"
                    borderless
                    left
                    outlined
                  >
                    <template #card>
                      <v-list
                        v-model:selected="filters.program_id"
                        density="compact"
                      >
                        <v-list-item
                          v-for="program in programs"
                          :key="program.id"
                          :title="$bct(program.name)"
                          :value="program.id"
                          color="primary"
                          data-cy="availability-checkmark"
                        >
                          <template #prepend="{ isSelected, select }">
                            <v-checkbox-btn
                              @update:model-value="select"
                              :model-value="isSelected"
                              density="compact"
                              tabindex="-1"
                            >
                              <template #input="{ inputNode, icon }">
                                <v-icon
                                  :icon="icon"
                                  size="x-small"
                                />

                                <component :is="inputNode" />
                              </template>
                            </v-checkbox-btn>
                          </template>
                        </v-list-item>
                      </v-list>
                    </template>
                  </FilterMenu>

                  <FilterMenu
                    :actions="false"
                    :title="statusFilterTitle"
                    elevation="0"
                    test-id="status-filter"
                    borderless
                    left
                    outlined
                    paddingless
                  >
                    <template #card>
                      <v-list
                        v-model:selected="filters.status"
                        density="compact"
                        select-strategy="classic"
                      >
                        <v-list-item
                          v-for="status in availableStatuses"
                          :key="status.value"
                          :title="$bct(status.text)"
                          :value="status.value"
                          color="primary"
                          data-cy="availability-checkmark"
                        >
                          <template #prepend="{ isSelected, select }">
                            <v-checkbox-btn
                              @update:model-value="select"
                              :model-value="isSelected"
                              density="compact"
                              tabindex="-1"
                            >
                              <template #input="{ inputNode, icon }">
                                <v-icon
                                  :icon="icon"
                                  size="x-small"
                                />

                                <component :is="inputNode" />
                              </template>
                            </v-checkbox-btn>
                          </template>
                        </v-list-item>
                      </v-list>
                    </template>
                  </FilterMenu>
                </div>
              </div>
              <div>
                <ActionMenu
                  @click:action:export="download('csv')"
                  :items="[{ title: 'Export to CSV for Excel', event: 'export' }]"
                  button-icon="more_vert"
                />
              </div>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <v-card
        class="mb-2"
        border
        tile
      >
        <DataTableServer
          v-model="checkedEnrollmentRequests"
          @click:row="handleRowClick"
          @update:page="handlePageChange"
          @update:sort-by="handleSortChange"
          :headers="tableHeaders"
          :hide-default-footer="!totalItemCount"
          :items="enrollmentRequests"
          :items-length="totalItemCount"
          :items-per-page="itemsPerPage"
          :loading="processing"
          :page="filters.page"
          :row-props="getRowProps"
          item-key="id"
          fixed-header
          return-object
          show-select
        >
          <template #[`item.name`]="{ item: enrollment }">
            {{ enrollment.name }}

            <v-badge
              v-if="!enrollment.seen_by_provider_at"
              :content="$bct('NEW')"
              :label="$bct('New enrollment')"
              color="primary"
              text-color="white"
              inline
            />
          </template>

          <template #[`item.subsidy_flags`]="{ item: enrollment }">
            <ComputedSubsidyFlags :enrollment="enrollment" />
          </template>

          <template #[`item.dob`]="{ item: enrollment }">
            {{ enrollment.meta.child.dob }}
          </template>

          <template #[`item.address`]="{ item: enrollment }">
            {{ enrollment.meta.group.home_address }} {{ enrollment.meta.group.home_city }}
          </template>

          <template #[`item.provider`]="{ item: enrollment }">
            {{ enrollment.meta.provider.name }}
          </template>

          <template #[`item.program`]="{ item: enrollment }">
            {{ enrollment.meta.program?.name }}
          </template>

          <template #[`item.status`]="{ item: enrollment }">
            {{ $bct(getStatusText(enrollment.status)) }}
          </template>

          <template #[`item.due_date`]="{ item: enrollment }">
            <LongDate :date="enrollment.due_date" />
          </template>

          <template #[`item.waitlist`]="{ item: enrollment }">
            <template v-if="enrollment.status == ENROLLMENT_STATUSES.WAITLISTED">
              <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledSelect -->
              <v-select
                v-model="enrollment.waitlist_number"
                @click.stop=""
                @update:model-value="setWaitlistNumber(enrollment, $event)"
                :items="waitlistNumbers"
                class="mxw-125"
                density="compact"
                variant="solo"
                hide-details
              />
            </template>
          </template>
        </DataTableServer>
      </v-card>
    </div>

    <ResourceDialog
      @save="createEnrollment"
      ref="newDialog"
      :fields="newEnrollmentFields"
      :max-width="600"
      :processing="processing"
      title="Add"
    />

    <ResourceDialog
      @save="closeInviteDialog()"
      ref="inviteDialog"
      :cancellable="false"
      :closeable="true"
      :max-width="600"
      :save-button-disabled="programs.length === 0"
      save-button-text="Done"
      subtitle="Use this referral link to invite a child to apply to your program."
      title="Invite a child"
      hide-actions
    >
      <template #form>
        <v-row>
          <v-col cols="12">
            <div class="fs-14">
              {{ `${$bct('Select a location')}:` }}
            </div>

            <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledSelect -->
            <v-select
              v-model="selectedInviteProviderId"
              :items="providers"
              class="b-radius-0"
              data-testid="select-provider"
              item-title="name"
              item-value="id"
              variant="solo"
              clearable
            />
          </v-col>
        </v-row>

        <div v-if="selectedInviteProviderId">
          <v-row
            v-for="program in inviteProgramOptions"
            :key="program?.id"
            dense
          >
            <CopyableInput
              :label="program?.name"
              :value="buildInviteLink(program)"
            />
          </v-row>
        </div>
      </template>
    </ResourceDialog>

    <ResourceDialog
      @save="sendMassMessage"
      ref="newMessageDialog"
      :processing="processing"
      :save-button-disabled="!massMessage.subject || !massMessage.body"
      save-button-text="Send"
      title="New message"
    >
      <template #form>
        <v-row>
          <LabeledTextfield
            v-model="massMessage.subject"
            message="Subject"
          />

          <LabeledTextarea
            v-model="massMessage.body"
            message="Body"
          />
        </v-row>
      </template>
    </ResourceDialog>

    <ResourceDialog
      @save="transferSelectedEnrollments()"
      ref="transferSelectedEnrollmentsDialog"
      :max-width="800"
      :processing="processing"
      save-button-text="Yes, transfer children"
      title="Transfer children"
      closeable
    >
      <template #form>
        <v-row>
          <span>
            {{
              $bct(
                'Selecting "Yes, transfer children" will move all children to the chosen location. Their status will not change.',
              )
            }}
          </span>

          <v-divider />

          <LabeledSimpleSelect
            v-model="selectedEnrollAllProviderId"
            :items="providers"
            :placeholder="$bct('Select a location...')"
            data-cy="provider-transfer-dropdown"
            item-title="name"
            item-value="id"
            message="Transfer to:"
            variant="filled"
          />
        </v-row>
      </template>
    </ResourceDialog>

    <EnrollmentRequestDialog
      @change="loadEnrollments"
      @decline="clearAndReload"
      ref="detailsDialog"
      :providers="providers"
      :statuses="availablePendingStatuses"
      :subsidy-program="selectedSubsidyProgram"
    />

    <CocVerificationDialog
      @change="loadEnrollments"
      ref="cocVerificationDialog"
      :subsidy-program="selectedSubsidyProgram"
    />

    <!-- TODO: Move into sub-component -->
    <v-dialog
      v-model="enrollAllDialogIsVisible"
      max-width="500"
      scrim="transparent"
    >
      <v-card
        v-if="checkedEnrollmentRequests.length > 0"
        class="mx-auto"
        max-width="500"
      >
        <v-card-title>{{ $bct(`${terms.enroll} all selected`) }}</v-card-title>
        <v-divider />

        <v-card-text class="pb-0">
          <p class="c-black fs-16 fw-500 mb-2">
            {{ $bct('Location') }}
          </p>

          <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledSelect -->
          <v-select
            v-model="selectedEnrollAllProviderId"
            :disabled="!store.state.pages.Interested.features.enable_provider_transfer"
            :items="providers"
            :readonly="!store.state.pages.Interested.features.enable_provider_transfer"
            item-title="name"
            item-value="id"
            variant="filled"
          />

          <div
            v-if="store.state.pages.Interested.features.enable_program_assignment"
            class="mb-6"
          >
            <p class="c-black fs-16 fw-500 mb-2">
              {{ $bct(`What program or class will they ${terms.enroll.toLowerCase()} in?`) }}
            </p>

            <!-- eslint-disable-next-line prefer-standard-components/no-vuetify-inputs -- TODO migrate to LabeledSelect -->
            <v-select
              v-model="programId"
              :disabled="!selectedEnrollAllProviderId"
              :items="enrollAllProgramOptions"
              item-title="name"
              item-value="id"
              variant="filled"
              hide-details
            />
          </div>
        </v-card-text>

        <v-card-actions class="pt-0">
          <v-btn
            @click="enrollAll"
            :loading="processing"
            color="primary"
            size="x-large"
            variant="flat"
            block
          >
            {{ $bct(`Yes, ${terms.enroll.toLowerCase()} these students`) }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup>
import { toRef } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';

import ActionMenu from '@/shared/components/ActionMenu.vue';
import CocVerificationDialog from '@/manager/components/enrollment/CocVerificationDialog.vue';
import ComputedSubsidyFlags from '@/manager/components/enrollment/ComputedSubsidyFlags.vue';
import CopyableInput from '@/shared/components/provider/CopyableInput.vue';
import { createDateTimeFileName } from '@/shared/services/date-time-file-name';
import DataTableServer from '@/shared/components/table/DataTableServer.vue';
import { ENROLLMENT_STATUSES } from '@/shared/assets/constants';
import EnrollmentRequestDialog from '@/manager/components/enrollment/EnrollmentRequestDialog.vue';
import FilterMenu from '@/shared/components/form/FilterMenu.vue';
import LabeledSimpleSelect from '@/shared/components/form/LabeledSimpleSelect.vue';
import LabeledTextarea from '@/shared/components/form/LabeledTextarea.vue';
import LabeledTextfield from '@/shared/components/form/LabeledTextfield.vue';
import LongDate from '@/shared/components/LongDate.vue';
import ResourceDialog from '@/shared/components/form/ResourceDialog.vue';
import useApi from '@/shared/composables/useApi';
import useEnrollmentStatuses from '@/shared/composables/useEnrollmentStatuses';
import useEventBus from '@/shared/composables/useEventBus';
import useRouterHelper from '@/shared/composables/useRouterHelper';
import useTerms from '@/shared/composables/useTerms';

const { api } = useApi();
const eventBus = useEventBus();
const route = useRoute();
const store = useStore();
const { terms } = useTerms();
const { updateQuery } = useRouterHelper();
const $bct = inject('translator');

const checkedEnrollmentRequests = ref([]);
const detailsDialog = ref(null);
const cocVerificationDialog = ref(null);
const enrollAllDialogIsVisible = ref(false);
const enrollAllProgramOptions = ref([]);
const enrollmentRequests = ref(null);
function normalizeStatusFromQuery(status) {
  if (!status) return [];
  return Array.isArray(status) ? status : [status];
}

/**
 * Status filter logic:
 *   Initial load (no query params): Show pending statuses [availablePendingStatuses via initializeStatusFilterWithDefaults]
 *   Initial load (w/ query params): Use query param statuses, filtered to remove disallowed [normalizeStatusFromQuery]
 *   Subsidy program switch: Update and show pending statuses [availablePendingStatuses].
 *   Unclick all statuses: Same as clicking all statuses: Show all [availableStatuses via activeStatusFilter]
 */
const filters = ref({
  provider_id: route.query.providerId,
  program_id: route.query.programId,
  status: normalizeStatusFromQuery(route.query.status),
  verification_status: null,
  sort_dir: 'asc',
  sort_field: 'waitlist',
  page: 1,
});
const inviteDialog = ref(null);
const inviteProgramOptions = ref([]);
const newDialog = ref(null);
const massMessage = ref({ subject: null, body: null });
const newMessageDialog = ref(null);
const itemsPerPage = ref(null);
const pageStartCount = ref(0);
const processing = ref(false);
const programId = ref(null);
const programs = ref([]);
const providerFilter = ref(null);
const query = ref(null);
const queryCount = ref(0);
const selectedEnrollAllProviderId = ref(null);
const selectedInviteProviderId = ref(null);
const transferSelectedEnrollmentsDialog = ref(null);
const totalItemCount = ref(0);
const verificationStatus = ref([
  { text: 'Verified', value: 'verified' },
  { text: 'Unverified', value: 'unverified' },
]);
const waitlistNumbers = ref([...Array(200).keys()].map((index) => index + 1));

const props = defineProps({
  displayCocVerification: {
    type: Boolean,
    default: false,
  },
  filterOptions: {
    type: Object,
    default: null,
  },
  providers: {
    type: Array,
    default: () => [],
  },
  selectedSubsidyProgram: {
    type: Object,
    default: () => ({}),
  },
});

const { allStatuses, pendingStatuses, selectedStatusObject, getStatusText } = useEnrollmentStatuses(
  { subsidyProgram: toRef(props, 'selectedSubsidyProgram') },
);

const cocVerificationStatusObject = {
  text: ENROLLMENT_STATUSES.SELECTED_VERIFICATION,
  value: ENROLLMENT_STATUSES.SELECTED_VERIFICATION,
  term: 'selected',
};

// Clean up unavailable statuses from URL on initial load. This happens when the
// configuration for a subsidy program changes and removes a status from the
// list of available statuses. When this happens, the URL will still have the
// status in the query string, so we need to remove it.
function removeUnavailableStatusFilters() {
  const availableValues = availableStatuses.value.map((s) => s.value);
  filters.value.status = filters.value.status.filter((s) => availableValues.includes(s));
}

watch(
  () => filters.value.provider_id,
  async (newProviderId, oldProviderId) => {
    if (newProviderId !== oldProviderId) {
      await loadPrograms(newProviderId);
      if (filters.value.program_id) {
        filters.value.program_id = null;
        return; // The change to filters program_id will trigger watch again
      }
    }
    void updateFiltersAndReload();
  },
  { immediate: true },
);

watch(
  [
    () => filters.value.program_id,
    () => filters.value.verification_status,
    () => filters.value.status,
    query,
  ],
  () => {
    void updateFiltersAndReload();
  },
  { deep: true },
);

watch(
  () => props.selectedSubsidyProgram,
  async () => {
    selectedInviteProviderId.value = null;
    inviteProgramOptions.value = [];
    checkedEnrollmentRequests.value = [];

    filters.value.verification_status = null;
    filters.value.status = availablePendingStatuses.value.map((s) => s.value);

    void loadEnrollments();
  },
);

watch(
  () => props.filterOptions,
  async () => {
    if (props.filterOptions) {
      filters.value = {
        ...props.filterOptions,
        status: normalizeStatusFromQuery(props.filterOptions.status),
      };
      await updateQuery(filters.value);
      void loadEnrollments();
    }
  },
);

watch(selectedEnrollAllProviderId, (newValue) => loadEnrollAllProgramOptions(newValue));

watch(selectedInviteProviderId, (newValue) => loadInviteProgramOptions(newValue));

const tableHeaders = computed(() => {
  const headers = [
    { title: 'Child', key: 'name' },
    { title: 'DOB', key: 'dob' },
    { title: 'Home', key: 'address' },
    { title: 'Location', key: 'provider' },
  ];

  if (hasAnyEnrollmentsWithSubsidyFlags.value && props.displayCocVerification) {
    headers.splice(1, 0, { title: 'Tags', value: 'subsidy_flags', width: '15%' });
  }

  if (props.selectedSubsidyProgram?.enable_deferred_acceptance) {
    headers.push({ title: terms.value.program, key: 'program' });
  }
  headers.push({ title: 'Status', key: 'status' });

  if (props.selectedSubsidyProgram?.uses_enrollment_due_dates) {
    headers.push({ title: 'Due', key: 'due_date' });
  }

  if (waitlistEnabled.value) {
    headers.push({ title: 'Waitlist', key: 'waitlist' });
  }

  return headers;
});

// When "Selected" is excluded by the subsidy program, re-add it (or a
// verification variant) based on which feature flags are enabled.
// Direct enrollment takes priority over CoC verification.
function withExclusionBypasses(statusObjects) {
  if (statusObjects.some((s) => s.value === ENROLLMENT_STATUSES.SELECTED)) {
    return statusObjects;
  }

  const sp = props.selectedSubsidyProgram;

  if (sp?.enable_provider_direct_enrollment_of_students) {
    return [...statusObjects, selectedStatusObject.value];
  }

  if (sp?.enable_provider_verification_of_coc_children) {
    return [...statusObjects, cocVerificationStatusObject];
  }

  return statusObjects;
}

const availableStatuses = computed(() => withExclusionBypasses(allStatuses.value));

const statusFilterTitle = computed(() => {
  const count = filters.value.status.length;
  return count > 0 ? `${count} selected` : 'Status';
});

const displayEditCheckedButton = computed(() => {
  if (props.selectedSubsidyProgram?.enable_deferred_acceptance) return false;

  return checkedEnrollmentRequests.value.length > 0;
});

const displayEditCheckedOptions = computed(() => {
  return checkedEnrollmentRequests.value.length > 0;
});

const enrollAllDisabled = computed(() => {
  return checkedEnrollmentRequests.value.some(
    (enrollmentRequest) =>
      enrollmentRequest.provider_id !== checkedEnrollmentRequests.value[0].provider_id,
  );
});

const newEnrollmentFields = computed(() => {
  return [
    {
      cols: '6',
      text: 'Parent first name',
      value: 'primary_first_name',
      required: true,
    },
    {
      cols: '6',
      text: 'Parent last name',
      value: 'primary_last_name',
      required: true,
    },
    { text: 'Parent email', value: 'primary_email' },
    { text: 'Parent phone', value: 'primary_phone' },
    {
      cols: '6',
      text: 'Child first name',
      value: 'child_first_name',
      required: true,
    },
    {
      cols: '6',
      text: 'Child last name',
      value: 'child_last_name',
      required: true,
    },
    {
      text: 'Child DOB',
      value: 'child_dob',
      required: true,
      type: 'dob',
    },
    {
      text: 'Location',
      value: 'provider_id',
      required: true,
      items: props.providers,
      itemText: 'name',
      itemValue: 'id',
    },
  ];
});

const filteredProgramName = computed(() => {
  if (filters.value.program_id && filters.value.provider_id) {
    return programs.value.find((program) => program.id === filters.value.program_id)?.name;
  }

  return null;
});

const filteredProviderName = computed(() => {
  if (filters.value.provider_id && props.providers.length > 0) {
    return props.providers.find((provider) => provider.id === filters.value.provider_id)?.name;
  }

  return null;
});

const filteredVerificationStatus = computed(() => {
  if (filters.value.verification_status) {
    return verificationStatus.value.find(
      (status) => status.value === filters.value.verification_status,
    )?.text;
  }

  return null;
});

const waitlistEnabled = computed(() => {
  return store.state.pages.Interested.features.enable_waitlist;
});

const hasAnyEnrollmentsWithSubsidyFlags = computed(() => {
  if (!enrollmentRequests.value) return false;

  return enrollmentRequests.value.some((enrollment) => {
    const { computed_subsidy_flags: computedSubsidyFlags } = enrollment?.meta || {};

    return (
      computedSubsidyFlags?.current_provider ||
      computedSubsidyFlags?.sibling_provider ||
      computedSubsidyFlags?.staff_provider ||
      computedSubsidyFlags?.iep
    );
  });
});

const filterSummary = computed(() => {
  const appliedFilters = [];
  if (filteredProviderName.value) appliedFilters.push(filteredProviderName.value);

  const statusValues = activeStatusFilter.value;
  if (statusValues.length > 0) {
    const statusText = statusValues
      .map((s) => $bct(getStatusText(s)))
      .filter(Boolean)
      .join(', ');
    if (statusText) {
      appliedFilters.push(statusText);
    }
  }

  if (filteredProgramName.value) appliedFilters.push(filteredProgramName.value);
  if (filteredVerificationStatus.value) appliedFilters.push($bct(filteredVerificationStatus.value));

  return appliedFilters.length > 0 ? appliedFilters.join(', ') : null;
});

const availablePendingStatuses = computed(() => withExclusionBypasses(pendingStatuses.value));

const defaultStatusValues = computed(() => {
  return availablePendingStatuses.value.map((s) => s.value);
});

const activeStatusFilter = computed(() => {
  if (filters.value.status.length > 0) return filters.value.status;
  return availableStatuses.value.map((s) => s.value);
});

function initializeStatusFilterWithDefaults() {
  if (filters.value.status.length === 0 && defaultStatusValues.value.length > 0) {
    filters.value.status = defaultStatusValues.value;
  }
}

watch(availablePendingStatuses, initializeStatusFilterWithDefaults, { immediate: true });

watch(() => props.selectedSubsidyProgram, removeUnavailableStatusFilters, { immediate: true });

const isVerifyButtonEnabled = computed(() => {
  return (
    checkedEnrollmentRequests.value.length === 1 &&
    checkedEnrollmentRequests.value[0].status === ENROLLMENT_STATUSES.SELECTED &&
    (checkedEnrollmentRequests.value[0].meta?.computed_subsidy_flags?.current_provider ||
      checkedEnrollmentRequests.value[0].meta?.computed_subsidy_flags?.sibling_provider ||
      checkedEnrollmentRequests.value[0].meta?.computed_subsidy_flags?.staff_provider)
  );
});

const verifyButtonTooltip = computed(() => {
  if (isVerifyButtonEnabled.value) {
    return null;
  }

  if (checkedEnrollmentRequests.value.length === 0) {
    return $bct('Choose a single child in Selected status to verify tags.');
  }

  if (checkedEnrollmentRequests.value.length > 1) {
    return $bct('Select only one child at a time to verify CoC tags');
  }

  return $bct(
    'Only children with Selected status can be verified. This child has a different status.',
  );
});

function areValidEnrollmentsWithoutSubsidyPrograms() {
  const validTransferStatuses = [
    ENROLLMENT_STATUSES.SELECTED,
    ENROLLMENT_STATUSES.PROPOSED,
    ENROLLMENT_STATUSES.PLACED,
    ENROLLMENT_STATUSES.OFFERED,
    ENROLLMENT_STATUSES.ACCEPTED,
  ];
  if (checkedEnrollmentRequests.value.length > 0) {
    return checkedEnrollmentRequests.value.every(
      (enrollment) =>
        enrollment.subsidy_program_id == null && validTransferStatuses.includes(enrollment.status),
    );
  }
}

async function clearAndReload() {
  await loadEnrollments();
  detailsDialog.value?.close();
}

function openVerifyDialog() {
  cocVerificationDialog.value?.open(checkedEnrollmentRequests.value[0]);
}

function closeInviteDialog() {
  selectedInviteProviderId.value = null;
  inviteDialog.value?.close();
}

function createEnrollment(newReq) {
  if (!newReq.primary_email && !newReq.primary_phone) {
    return eventBus.chime('A parent email or phone number is required');
  }

  processing.value = true;

  const thisNewRew = newReq;
  thisNewRew.status = ENROLLMENT_STATUSES.SELECTED;

  api.manager.enrollment.create(
    thisNewRew,
    async () => {
      newDialog.value.close();
      processing.value = false;
      eventBus.chime('Added');
      await loadEnrollments();
    },
    (error) => {
      processing.value = false;
      if (error.response.data.errors[0] === 'Family already exists') {
        eventBus.chime(store.state.pages.Interested.content.duplicate_family_message);
      } else {
        eventBus.chime(error.response.data.errors[0]);
      }
    },
  );
  return true;
}

function buildInviteLink(program) {
  const baseUrl = store.state.config?.parent_url;
  const { subsidyProgramId } = route.query;
  const path = `/provider_referral?subsidyProgramId=${subsidyProgramId}&programId=${program?.id}`;

  return `${baseUrl}${path}`;
}

function draftEnrollAll() {
  selectedEnrollAllProviderId.value = checkedEnrollmentRequests.value[0].provider_id;
  enrollAllDialogIsVisible.value = true;
}

async function download(format) {
  const allowed = ['program_id', 'provider_id', 'verification_status'];

  const pickedFilters = allowed.reduce((acc, key) => {
    const value = filters.value[key];
    if (value != null && value !== '') {
      acc[key] = value;
    }
    return acc;
  }, {});

  const downloadParams = {
    ...pickedFilters,
    include: 'computed_subsidy_flags',
    subsidy_program_id: props.selectedSubsidyProgram?.id,
    status: activeStatusFilter.value,
    tab: 'interested',
  };

  const filename = createDateTimeFileName(
    store.state.pages.Interested.title || 'Interested',
    store.state.profile.default_locale,
  );

  const url = api.manager.enrollment.downloadUrl(downloadParams, filename, format);
  window.open(url);
}

async function enrollAll() {
  processing.value = true;

  const providerId = checkedEnrollmentRequests.value[0].provider_id;
  await Promise.all(
    checkedEnrollmentRequests.value.map(async (enrollmentRequest) => {
      api.manager.enrollment.update(enrollmentRequest.id, {
        provider_id: providerId,
        program_id: programId.value,
        status: ENROLLMENT_STATUSES.ENROLLED,
      });
    }),
  );
  setTimeout(async () => {
    enrollAllDialogIsVisible.value = false;
    await loadEnrollments();
  }, 2000);
}

function getProviderNameWithAddress(provider) {
  return [provider.name, provider.address1].filter(Boolean).join(' - ');
}

function getRowProps({ item: enrollment }) {
  if (enrollment.seen_by_provider_at) return {};
  return { class: 'fw-900' };
}

function handlePageChange(newPageValue) {
  filters.value.page = newPageValue;
  void updateFiltersAndReload(true);
}

function handleRowClick(_event, { item: enrollment }) {
  updateQuery({ enrollmentId: enrollment.id });
}

function handleSortChange(sorts) {
  // Note: we currently only support a single sort/dir
  // so we only access the first item in passed array
  if (sorts.length === 0) {
    filters.value.sort_field = null;
    filters.value.sort_dir = null;
  } else {
    filters.value.sort_field = sorts[0].key;
    filters.value.sort_dir = sorts[0].order;
  }
  processing.value = true;
  void updateFiltersAndReload();
}

async function loadEnrollments() {
  processing.value = true;

  if (
    filters.value.provider_id &&
    !props.providers.find((provider) => provider.id === filters.value.provider_id)
  ) {
    filters.value.provider_id = null;
    return; // The change above will trigger watch that calls load
  }

  filters.value.query = query.value;
  queryCount.value += 1;
  const currentQueryCount = queryCount.value;
  const newFilters = { ...filters.value };
  newFilters.status = activeStatusFilter.value;

  if (props.selectedSubsidyProgram?.id) {
    newFilters.subsidy_program_id = props.selectedSubsidyProgram?.id;
  }

  api.manager.enrollment.index(
    { ...newFilters, include: 'computed_subsidy_flags', tab: 'interested' },
    (response) => {
      if (queryCount.value === currentQueryCount) {
        enrollmentRequests.value = response.data;
        itemsPerPage.value = parseInt(response.headers['x-page-size'] || 0, 10);
        totalItemCount.value = parseInt(response.headers['x-total-count'] || 0, 10);
        pageStartCount.value = itemsPerPage.value * (filters.value.page - 1) + 1;

        checkedEnrollmentRequests.value = [];
        processing.value = false;
      }
    },
  );
}

async function loadEnrollAllProgramOptions(providerId) {
  if (!providerId) {
    enrollAllProgramOptions.value = [];
    return;
  }

  const programFilters = {
    subsidy_program_id: props.selectedSubsidyProgram?.id,
  };
  const { data } = await api.program.index(providerId, programFilters);
  enrollAllProgramOptions.value = data;
}

async function loadInviteProgramOptions(providerId) {
  if (!providerId) {
    inviteProgramOptions.value = [];
    return;
  }

  const programFilters = {
    subsidy_program_id: props.selectedSubsidyProgram?.id,
  };
  const { data } = await api.program.index(providerId, programFilters);
  inviteProgramOptions.value = data;
}

async function loadPrograms(providerId) {
  if (!providerId) {
    programs.value = [];
    return;
  }

  const programFilters = {};
  if (props.selectedSubsidyProgram)
    programFilters.subsidy_program_id = props.selectedSubsidyProgram.id;

  const { data } = await api.program.index(providerId, programFilters);
  programs.value = data;
}

function sendMassMessage() {
  processing.value = true;
  massMessage.value.enrollment_ids = checkedEnrollmentRequests.value.map(
    (enrollmentRequest) => enrollmentRequest.id,
  );
  api.manager.mass_message.create(
    massMessage.value,
    async () => {
      newMessageDialog.value.close();
      processing.value = false;
      eventBus.chime('Message sent');
      await loadEnrollments();
    },
    (error) => {
      processing.value = false;
      eventBus.chime(error.response.data.errors.join(', '));
    },
  );
}

async function setStatusForAll(status) {
  processing.value = true;
  await Promise.all(
    checkedEnrollmentRequests.value.map(async (enrollmentRequest) =>
      api.manager.enrollment.update(enrollmentRequest.id, { status }),
    ),
  );

  processing.value = false;
  await loadEnrollments();
}

async function setWaitlistNumber(enrollmentRequest, number) {
  await api.manager.enrollment.update(enrollmentRequest.id, { waitlist_number: number });
  await loadEnrollments();
}

async function transferSelectedEnrollments() {
  processing.value = true;

  const providerId = selectedEnrollAllProviderId.value;
  await Promise.all(
    checkedEnrollmentRequests.value.map(async (enrollmentRequest) => {
      api.manager.enrollment.update(enrollmentRequest.id, {
        provider_id: providerId,
      });
    }),
  );

  setTimeout(async () => {
    transferSelectedEnrollmentsDialog.value?.close();
    await loadEnrollments();
  }, 2000);
}

async function updateFiltersAndReload(paged = false) {
  if (!paged) filters.value.page = 1;
  await updateQuery(filters.value);
  void loadEnrollments();
}
</script>
